/**
 * EnrollmentLinkModal — owner-only modal that sends a parent a one-tap "Enroll & pay" link.
 *
 * Accepts EITHER:
 *   - athleteId  → modal fetches the dossier to find parent_user_id (legacy path)
 *   - parentUserId → modal uses this directly (lead-detail path)
 *
 * Picks between 3 option groups:
 *   1. Pricing matrix SKU (premium — Stripe-synced, with 10.3% tax)
 *   2. Subscription plan (legacy)
 *   3. Session pack (legacy)
 *
 * Selecting one auto-clears the other two.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollmentLinkModal({ athleteId, parentUserId: parentUserIdProp, onClose }) {
    const [plans, setPlans] = useState([]);
    const [packs, setPacks] = useState([]);
    const [matrixSkus, setMatrixSkus] = useState([]);
    // SKUs that are active + priced but NOT yet synced to Stripe — we surface
    // these to the owner so they can fix in one click instead of being told
    // "No SKUs are active" (the bug Sachin hit on 2026-05-26).
    const [unsyncedSkus, setUnsyncedSkus] = useState([]);
    const [busy, setBusy] = useState(true);
    const [planId, setPlanId] = useState("");
    const [packId, setPackId] = useState("");
    const [skuId, setSkuId] = useState("");
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [parentUserId, setParentUserId] = useState(parentUserIdProp || null);
    // Per-athlete active subscription — when truthy, we grey out subscription
    // options so the coach doesn't accidentally double-bill the family.
    // Populated only when an athleteId is supplied (athlete dossier flow).
    // Sachin spec 2026-02-17.
    const [athleteSub, setAthleteSub] = useState(null);

    const loadCatalogs = useCallback(async () => {
        const [p, pk, mx] = await Promise.all([
            axios.get(`${API}/coach/billing/plans`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/coach/billing/packs`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/coach/pricing/matrix`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setPlans((p.data || []).filter((x) => x.active));
        setPacks((pk.data || []).filter((x) => x.active));
        const ready = [];
        const unsynced = [];
        (mx.data?.categories || []).forEach((cat) => {
            cat.skus.forEach((s) => {
                if (!s.active || !(s.price_cents > 0)) return;
                if (s.stripe_price_id) {
                    ready.push({ ...s, category_label: cat.label, category_key: cat.key });
                } else {
                    unsynced.push({ ...s, category_label: cat.label, category_key: cat.key });
                }
            });
        });
        setMatrixSkus(ready);
        setUnsyncedSkus(unsynced);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // If parentUserId came in directly, skip the athlete-resolution step.
                if (!parentUserIdProp && athleteId) {
                    const dossier = await axios.get(`${API}/coach/athletes/${athleteId}`, { withCredentials: true });
                    if (cancelled) return;
                    setParentUserId(dossier.data?.parent?.user_id);
                }
                // Per-athlete subscription lookup (athlete dossier flow only).
                // Used to grey out subscription options when already covered.
                if (!cancelled && athleteId) {
                    try {
                        const billing = await axios.get(
                            `${API}/coach/athletes/${athleteId}/billing`,
                            { withCredentials: true },
                        );
                        if (!cancelled) setAthleteSub(billing.data?.subscription || null);
                    } catch {
                        if (!cancelled) setAthleteSub(null);
                    }
                }
                if (cancelled) return;
                await loadCatalogs();
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [athleteId, parentUserIdProp, loadCatalogs]);

    const syncAllToStripe = async () => {
        setSyncing(true);
        try {
            const r = await axios.post(`${API}/coach/pricing/matrix/sync-all`, {}, { withCredentials: true });
            const { synced_count = 0, failed_count = 0 } = r.data || {};
            if (failed_count > 0) {
                toast.warning(`Synced ${synced_count}, but ${failed_count} failed`, {
                    description: "Check the Pricing Matrix page for details.",
                });
            } else {
                toast.success(`Synced ${synced_count} SKU${synced_count === 1 ? "" : "s"} to Stripe`);
            }
            await loadCatalogs();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Sync failed. Check your Stripe key.";
            toast.error("Sync failed", { description: detail });
        } finally {
            setSyncing(false);
        }
    };

    const totalOptions = plans.length + packs.length + matrixSkus.length;

    const send = async () => {
        if (!parentUserId) { toast.error("No parent account found"); return; }
        if (!planId && !packId && !skuId) { toast.error("Pick a plan, pack, or matrix SKU"); return; }
        setSending(true);
        try {
            if (skuId) {
                await axios.post(
                    `${API}/coach/leads/${parentUserId}/send-matrix-enrollment-link`,
                    { sku_id: skuId, custom_note: note },
                    { withCredentials: true },
                );
            } else {
                await axios.post(
                    `${API}/coach/leads/${parentUserId}/send-enrollment-link`,
                    { plan_id: planId || null, pack_id: packId || null, custom_note: note },
                    { withCredentials: true },
                );
            }
            toast.success("Enrollment link sent");
            onClose?.();
        } catch (e) {
            toast.error("Couldn't send", { description: e?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="enrollment-modal" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-lg">
                <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Enrollment</div>
                        <h4 className="font-display text-xl tracking-wider mt-0.5">Send pay link</h4>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white">✕</button>
                </div>
                <div className="p-5 space-y-4">
                    {busy ? (
                        <div className="inline-flex items-center gap-2 text-zinc-300 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                    ) : totalOptions === 0 && unsyncedSkus.length > 0 ? (
                        // You have SKUs but none are synced to Stripe yet — the common
                        // post-"Reset to canonical 8 SKUs" state. Offer a one-click sync
                        // right here so the owner doesn't have to navigate away.
                        <div
                            data-testid="enrollment-needs-sync"
                            className="text-sm bg-amber-900/15 border border-amber-600/50 rounded-lg p-4 space-y-3"
                        >
                            <div className="text-amber-200">
                                You have <strong>{unsyncedSkus.length}</strong> active SKU{unsyncedSkus.length === 1 ? "" : "s"} in your pricing matrix, but they aren&apos;t synced to Stripe yet — so we can&apos;t charge anyone with them.
                            </div>
                            <div className="text-[10px] tracking-wider text-zinc-300">
                                Sample: {unsyncedSkus.slice(0, 3).map((s) => `${s.display_name} ($${(s.price_cents / 100).toFixed(0)})`).join(" · ")}{unsyncedSkus.length > 3 ? ` · +${unsyncedSkus.length - 3} more` : ""}
                            </div>
                            <button
                                type="button"
                                onClick={syncAllToStripe}
                                disabled={syncing}
                                data-testid="enrollment-sync-all"
                                className="btn-tactical text-xs inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                Sync {unsyncedSkus.length} SKU{unsyncedSkus.length === 1 ? "" : "s"} to Stripe now
                            </button>
                        </div>
                    ) : totalOptions === 0 ? (
                        <div className="text-sm text-amber-300 bg-amber-900/10 border border-amber-600/40 rounded-lg px-3 py-2">
                            No plans, packs, or matrix SKUs are active yet. Add some at <code>/coach/billing/plans</code>, <code>/coach/billing/packs</code>, or <code>/coach/pricing</code> first.
                        </div>
                    ) : (
                        <>
                            {athleteSub && (athleteSub.status === "active" || athleteSub.status === "trialing") && (
                                <div
                                    data-testid="enrollment-already-subscribed-banner"
                                    className="text-[11px] bg-emerald-900/15 border border-emerald-600/40 rounded px-3 py-2 text-emerald-200 leading-snug"
                                >
                                    <span className="font-semibold">Already subscribed · Auto-renews.</span>{" "}
                                    Subscription options are greyed out — pick a session pack or one-off SKU instead.
                                </div>
                            )}
                            {unsyncedSkus.length > 0 && (
                                <div
                                    data-testid="enrollment-partial-sync-warning"
                                    className="text-[11px] bg-amber-900/15 border border-amber-600/40 rounded px-3 py-2 flex items-center justify-between gap-3"
                                >
                                    <span className="text-amber-200">
                                        {unsyncedSkus.length} SKU{unsyncedSkus.length === 1 ? "" : "s"} not synced to Stripe yet — hidden below.
                                    </span>
                                    <button
                                        type="button"
                                        onClick={syncAllToStripe}
                                        disabled={syncing}
                                        data-testid="enrollment-sync-partial"
                                        className="text-[10px] tracking-[0.2em] uppercase text-amber-200 hover:text-white inline-flex items-center gap-1.5 disabled:opacity-60"
                                    >
                                        {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                        Sync now
                                    </button>
                                </div>
                            )}
                            {matrixSkus.length > 0 && (
                                <div data-testid="enrollment-matrix-section">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1 flex items-center gap-2">
                                        <span>Pricing matrix SKU</span>
                                        <span className="text-[9px] bg-emerald-900/40 text-emerald-300 px-1.5 py-0.5 rounded">Premium</span>
                                    </div>
                                    <select
                                        data-testid="enrollment-matrix-sku"
                                        value={skuId}
                                        onChange={(e) => { setSkuId(e.target.value); if (e.target.value) { setPlanId(""); setPackId(""); } }}
                                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                                    >
                                        <option value="">— Select a matrix SKU —</option>
                                        {matrixSkus.map((s) => {
                                            const isSubSku = s.category_key === "member_subscription";
                                            const blocked = isSubSku && athleteSub && (athleteSub.status === "active" || athleteSub.status === "trialing");
                                            return (
                                                <option
                                                    key={s.sku_id}
                                                    value={s.sku_id}
                                                    disabled={blocked || undefined}
                                                    data-testid={`enrollment-matrix-sku-option-${s.sku_id}`}
                                                >
                                                    {s.display_name} — ${(s.price_cents / 100).toFixed(2)} + ${(s.totals.tax_cents / 100).toFixed(2)} tax = ${(s.totals.total_cents / 100).toFixed(2)}
                                                    {blocked ? "  · Already subscribed" : ""}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <div className="text-[10px] text-zinc-500 mt-1">Synced to Stripe · 10.3% tax added at checkout</div>
                                </div>
                            )}
                            {plans.length > 0 && (() => {
                                const subActive = !!(athleteSub && (athleteSub.status === "active" || athleteSub.status === "trialing"));
                                return (
                                    <div
                                        data-testid="enrollment-legacy-plan-section"
                                        className={subActive ? "opacity-50 pointer-events-none" : ""}
                                        aria-disabled={subActive || undefined}
                                    >
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1 flex items-center gap-2">
                                            <span>Subscription plan (legacy)</span>
                                            {subActive && (
                                                <span
                                                    data-testid="enrollment-legacy-plan-subscribed-label"
                                                    className="text-[9px] tracking-[0.18em] uppercase text-emerald-300 border border-emerald-600/40 px-1.5 py-0.5"
                                                >
                                                    Already subscribed · Auto-renews
                                                </span>
                                            )}
                                        </div>
                                        <select
                                            data-testid="enrollment-plan"
                                            value={planId}
                                            disabled={subActive}
                                            onChange={(e) => { setPlanId(e.target.value); if (e.target.value) { setPackId(""); setSkuId(""); } }}
                                            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red disabled:cursor-not-allowed"
                                        >
                                            <option value="">— Select a plan —</option>
                                            {plans.map((p) => (
                                                <option key={p.plan_id} value={p.plan_id}>{p.name} — ${(p.price_cents / 100).toFixed(2)}/{p.interval || "month"}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })()}
                            {packs.length > 0 && (
                                <div>
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1">Session pack (legacy)</div>
                                    <select
                                        data-testid="enrollment-pack"
                                        value={packId}
                                        onChange={(e) => { setPackId(e.target.value); if (e.target.value) { setPlanId(""); setSkuId(""); } }}
                                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                                    >
                                        <option value="">— Select a pack —</option>
                                        {packs.map((p) => (
                                            <option key={p.pack_id} value={p.pack_id}>{p.name} — ${(p.price_cents / 100).toFixed(2)}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div>
                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1">Custom note (optional)</div>
                                <textarea
                                    rows={3}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="A quick line from you — appears above the Enroll button."
                                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-ghost-tactical text-xs">Cancel</button>
                    <button
                        type="button"
                        data-testid="send-enrollment"
                        onClick={send}
                        disabled={sending || busy || totalOptions === 0 || (!planId && !packId && !skuId)}
                        className="btn-tactical text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                        Send link
                    </button>
                </div>
            </div>
        </div>
    );
}
